/**
 * @fileoverview Shared `.sd-takeaway` callout builder ("Why This Matters",
 * "What We're Testing", …).
 *
 * Single source of truth for the callout markup used by the /practices detail
 * panel and by the in-chat practice-proposal card, so the two stay visually
 * identical. Two flavours:
 *   - static      → label + text, always visible
 *   - collapsible → label + chevron header row; clicking the box toggles
 *                   `aria-expanded`, which drives text visibility and chevron
 *                   orientation in CSS (`.sd-takeaway--collapsible`, see
 *                   css/features/discovery.css).
 *
 * Safety: all caller text is inserted via `textContent`, never `innerHTML`.
 *
 * @module components/takeawayBox
 */

const CHEVRON_SVG = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
  <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/**
 * Build a takeaway callout element.
 *
 * A `hue` turns the box into the CALLOUT form (`.sd-takeaway--callout`,
 * components/takeaway.css): its own rounded container with the eyebrow in
 * the hue's ink, the hue named by what the callout says — `measure` (how
 * you'd know), `point` (what rides on it) or `claim` (the claim under test).
 * Colour is semantic on the detail pages (style guide §2), so a caller picks
 * the hue by meaning, never by taste.
 *
 * @param {Object} options
 * @param {string} options.label - Eyebrow label (e.g. 'Why This Matters')
 * @param {string} options.text - Body copy
 * @param {string} [options.modifier] - Extra class(es) (e.g. 'sd-takeaway--notice')
 * @param {boolean} [options.collapsible=false] - Render the clickable header + chevron
 * @param {boolean} [options.expanded=true] - Initial state when collapsible
 * @param {'measure'|'point'|'claim'|null} [options.hue=null] - Callout hue;
 *   omitted → the plain plate form
 * @returns {HTMLElement}
 */
export function buildTakeawayBox({ label, text, modifier = '', collapsible = false, expanded = true, hue = null }) {
  const box = document.createElement('div');
  box.className = [
    'sd-takeaway',
    collapsible ? 'sd-takeaway--collapsible' : '',
    hue ? `sd-takeaway--callout sd-takeaway--hue-${hue}` : '',
    modifier
  ]
    .filter(Boolean)
    .join(' ');

  const labelEl = document.createElement('div');
  labelEl.className = 'sd-takeaway__label';
  labelEl.textContent = label;

  if (collapsible) {
    box.setAttribute('data-takeaway-toggle', '');
    box.setAttribute('aria-expanded', expanded ? 'true' : 'false');

    const header = document.createElement('div');
    header.className = 'sd-takeaway__header';
    header.appendChild(labelEl);

    const chevron = document.createElement('span');
    chevron.className = 'sd-takeaway__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML = CHEVRON_SVG;
    header.appendChild(chevron);

    box.appendChild(header);
  } else {
    box.appendChild(labelEl);
  }

  const textEl = document.createElement('p');
  textEl.className = 'sd-takeaway__text';
  textEl.textContent = text;
  box.appendChild(textEl);

  return box;
}

/**
 * Several collapsible callouts sharing ONE plate instead of stacking their own.
 *
 * The group owns the ground and the padding; each child keeps the identical
 * collapsible markup `buildTakeawayBox` produces — same header row, same
 * chevron, same `[data-takeaway-toggle]`, so existing click delegation reaches
 * them unchanged — and gives up only its surface, in CSS
 * (`.sd-takeaway--group`, components/takeaway.css). A hairline separates the
 * children.
 *
 * A group of one is just a box: callers should render `buildTakeawayBox`
 * directly rather than a single-child group, or the lone child pays for a
 * wrapper that draws nothing extra. That convention covers the LEDE-LESS path
 * only. A `lede` is the group's own first child, so a caller passing one
 * ALWAYS renders a group — there is no box form that can carry it, and the
 * hairline under the lede is what makes it one plate rather than a headline
 * floating above a tile.
 *
 * The lede sits OUTSIDE every `[data-takeaway-toggle]` child, so clicking it
 * toggles nothing.
 *
 * @param {Object} options
 * @param {Array<Object>} options.parts - One {@link buildTakeawayBox} options
 *   object per child, in render order
 * @param {string} [options.modifier] - Extra class(es) for the GROUP element,
 *   whose ground and label colour cascade to the children
 * @param {string} [options.lede] - Optional serif headline rendered as
 *   `.sd-plate-lede.sd-plate-lede--plain`, the group's first child
 * @returns {HTMLElement}
 */
export function buildTakeawayGroup({ parts = [], modifier = '', lede = '' }) {
  const group = document.createElement('div');
  group.className = ['sd-takeaway', 'sd-takeaway--group', modifier].filter(Boolean).join(' ');
  if (lede) {
    const ledeEl = document.createElement('p');
    ledeEl.className = 'sd-plate-lede sd-plate-lede--plain';
    ledeEl.textContent = lede;
    group.appendChild(ledeEl);
  }
  for (const part of parts) {
    group.appendChild(buildTakeawayBox(part));
  }
  return group;
}

/**
 * String form of {@link buildTakeawayGroup}.
 *
 * @param {Object} options - Same shape as {@link buildTakeawayGroup}
 * @returns {string}
 */
export function takeawayGroupHtml(options) {
  return buildTakeawayGroup(options).outerHTML;
}

/**
 * String form of {@link buildTakeawayBox}, for callers that assemble markup
 * with template literals (e.g. PracticesSection.renderDetail).
 *
 * @param {Object} options - Same shape as {@link buildTakeawayBox}
 * @returns {string}
 */
export function takeawayBoxHtml(options) {
  return buildTakeawayBox(options).outerHTML;
}

/**
 * Flip a collapsible takeaway between expanded and collapsed.
 *
 * @param {HTMLElement} box - Element carrying `[data-takeaway-toggle]`
 */
export function toggleTakeawayBox(box) {
  const isExpanded = box.getAttribute('aria-expanded') === 'true';
  box.setAttribute('aria-expanded', String(!isExpanded));
}

/**
 * Wire a collapsible takeaway to toggle on click. Used by callers that insert
 * the element directly (chat cards); page sections that already delegate clicks
 * can call {@link toggleTakeawayBox} from their own handler instead.
 *
 * @param {HTMLElement} box - Element returned by {@link buildTakeawayBox}
 * @returns {HTMLElement} the same element, for chaining
 */
export function wireTakeawayToggle(box) {
  box.addEventListener('click', () => toggleTakeawayBox(box));
  return box;
}
